/**
 * 모바일 — 예약현황 (월 캘린더)
 * 날짜를 누르면 그날 예약 목록. 직접 등록 + (추후) 네이버예약 동기화 데이터가 같은 목록에 섞여 보인다.
 * 데이터 계약: shop/reservationStore.js (source/externalId 로 외부 예약 식별)
 */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { showAlert } from 'utils/toastBridge';
import { today } from 'shop/shopStore';
import {
  loadReservations, addReservation, updateReservation, removeReservation,
  RES_STATUS_OPTIONS, statusLabel, SOURCE_LABEL,
} from 'shop/reservationStore';
import { Page, H, CardBox, Field, Btn, Chip, Grid2, M } from './mobileUi';

const p2 = (n) => String(n).padStart(2, '0');
const DOW = ['일', '월', '화', '수', '목', '금', '토'];

const Cal = styled.div`
  background: #fff;
  border: 1px solid ${M.line};
  border-radius: 2px;
  padding: 10px 8px 12px;
  .head { display: flex; align-items: center; justify-content: space-between; padding: 0 4px 8px; }
  .head b { font-family: ${M.serif}; font-size: 20px; font-weight: 500; letter-spacing: 0.06em; }
  .head button { min-width: 44px; min-height: 44px; border: 0; background: transparent; font-size: 22px; color: ${M.text}; }
  .grid { display: grid; grid-template-columns: repeat(7, 1fr); row-gap: 2px; text-align: center; }
  .dow { font-size: 11px; letter-spacing: 0.1em; color: ${M.muted}; padding: 4px 0; }
  .dow.sun, .d.sun { color: ${M.red}; }
  .d {
    min-height: 46px; border: 0; background: transparent; color: ${M.text};
    display: flex; flex-direction: column; align-items: center; justify-content: flex-start; gap: 3px;
    padding-top: 6px; font-size: 15px; position: relative;
  }
  .d.out { visibility: hidden; }
  .d .num { width: 28px; height: 28px; line-height: 28px; border-radius: 50%; }
  .d.today .num { border: 1px solid ${M.gold}; }
  .d.sel .num { background: ${M.red}; color: ${M.cream}; border-color: ${M.red}; }
  .d .dots { display: flex; gap: 2px; height: 5px; }
  .d .dot { width: 5px; height: 5px; border-radius: 50%; background: ${M.red}; }
  .d .dot.naver { background: ${M.gold}; }
  .d .cnt { font-size: 10px; color: ${M.muted}; }
`;

const Legend = styled.div`
  display: flex; gap: 14px; font-size: 12px; color: ${M.muted};
  i { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-right: 4px; background: ${M.red}; }
  i.n { background: ${M.gold}; }
`;

const STATUS_WARN = { CANCELED: true, NOSHOW: true };

const MobileReservation = ({ tc }) => {
  const now = today();
  const [ym, setYm] = useState({ y: Number(now.slice(0, 4)), m: Number(now.slice(5, 7)) });
  const [sel, setSel] = useState(now);
  const [adding, setAdding] = useState(false);
  const [tick, setTick] = useState(0);
  const [form, setForm] = useState({ time: '10:00', customerNm: '', phone: '', serviceNm: '', staffNm: '', memo: '' });

  const first = `${ym.y}-${p2(ym.m)}-01`;
  const last = `${ym.y}-${p2(ym.m)}-${p2(new Date(ym.y, ym.m, 0).getDate())}`;
  const monthRows = useMemo(() => loadReservations(tc, first, last), [tc, first, last, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const byDay = useMemo(() => {
    const map = new Map();
    for (const r of monthRows) {
      const d = String(r.startDt).slice(0, 10);
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(r);
    }
    return map;
  }, [monthRows]);

  const cells = useMemo(() => {
    const lead = new Date(ym.y, ym.m - 1, 1).getDay();
    const days = new Date(ym.y, ym.m, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  }, [ym]);

  const move = (d) => {
    const dt = new Date(ym.y, ym.m - 1 + d, 1);
    setYm({ y: dt.getFullYear(), m: dt.getMonth() + 1 });
  };

  const dayRows = (byDay.get(sel) || []).slice();
  const activeCnt = (rows) => rows.filter((r) => r.status !== 'CANCELED').length;

  const submit = () => {
    if (!form.customerNm.trim()) return showAlert('예약자 이름을 입력하세요.', 'warning');
    if (!/^\d{2}:\d{2}$/.test(form.time)) return showAlert('시간을 확인하세요.', 'warning');
    addReservation(tc, {
      startDt: `${sel} ${form.time}`,
      customerNm: form.customerNm.trim(),
      phone: form.phone.replace(/\D/g, ''),
      serviceNm: form.serviceNm.trim(),
      staffNm: form.staffNm.trim(),
      memo: form.memo,
    });
    setForm({ ...form, customerNm: '', phone: '', serviceNm: '', memo: '' });
    setAdding(false);
    setTick((t) => t + 1);
    return showAlert('예약을 등록했습니다.', 'success');
  };

  const setStatus = (r, status) => {
    updateReservation(tc, r.reservationUid, { status });
    setTick((t) => t + 1);
  };
  const remove = (r) => {
    if (!window.confirm(`${r.customerNm} 예약을 삭제할까요?`)) return;
    removeReservation(tc, r.reservationUid);
    setTick((t) => t + 1);
  };

  return (
    <Page>
      <H>예약 현황</H>
      <Cal>
        <div className="head">
          <button type="button" onClick={() => move(-1)} aria-label="이전 달">‹</button>
          <b>{ym.y}. {p2(ym.m)}</b>
          <button type="button" onClick={() => move(1)} aria-label="다음 달">›</button>
        </div>
        <div className="grid">
          {DOW.map((d, i) => <div key={d} className={`dow ${i === 0 ? 'sun' : ''}`}>{d}</div>)}
          {cells.map((day, i) => {
            if (!day) return <div key={`e${i}`} className="d out" />;
            const key = `${ym.y}-${p2(ym.m)}-${p2(day)}`;
            const rows = byDay.get(key) || [];
            const live = rows.filter((r) => r.status !== 'CANCELED');
            const hasNaver = live.some((r) => r.source === 'NAVER');
            const hasManual = live.some((r) => r.source !== 'NAVER');
            return (
              <button
                key={key}
                type="button"
                className={`d ${i % 7 === 0 ? 'sun' : ''} ${key === now ? 'today' : ''} ${key === sel ? 'sel' : ''}`}
                onClick={() => setSel(key)}
              >
                <span className="num">{day}</span>
                <span className="dots">
                  {hasManual && <span className="dot" />}
                  {hasNaver && <span className="dot naver" />}
                </span>
                {live.length > 0 && <span className="cnt">{live.length}건</span>}
              </button>
            );
          })}
        </div>
      </Cal>
      <Legend><span><i />직접 등록</span><span><i className="n" />네이버예약</span></Legend>

      <H style={{ marginTop: 6 }}>{sel.slice(5).replace('-', '월 ')}일 · {activeCnt(dayRows)}건</H>
      <Btn onClick={() => setAdding(!adding)}>{adding ? '닫기' : '＋ 예약 등록'}</Btn>
      {adding && (
        <CardBox>
          <Grid2>
            <Field>시간<input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></Field>
            <Field>담당 원장<input value={form.staffNm} onChange={(e) => setForm({ ...form, staffNm: e.target.value })} /></Field>
          </Grid2>
          <Field>예약자<input value={form.customerNm} onChange={(e) => setForm({ ...form, customerNm: e.target.value })} /></Field>
          <Field>핸드폰<input inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field>서비스<input value={form.serviceNm} onChange={(e) => setForm({ ...form, serviceNm: e.target.value })} /></Field>
          <Field>메모<input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} /></Field>
          <Btn $primary onClick={submit}>{sel} 예약 저장</Btn>
        </CardBox>
      )}
      {dayRows.length === 0 && <span className="s">이 날의 예약이 없습니다.</span>}
      {dayRows.map((r) => (
        <CardBox key={r.reservationUid} $warn={!!STATUS_WARN[r.status]}>
          <span className="t">
            {String(r.startDt).slice(11, 16)} · {r.customerNm}{' '}
            <Chip $warn={!!STATUS_WARN[r.status]}>{statusLabel(r.status)}</Chip>{' '}
            <Chip>{SOURCE_LABEL[r.source] || r.source}</Chip>
          </span>
          <span className="s">{[r.serviceNm, r.staffNm && `담당 ${r.staffNm}`].filter(Boolean).join(' · ') || '-'}</span>
          {r.phone && <span className="s">{r.phone}</span>}
          {r.memo && <span className="s">{r.memo}</span>}
          <Grid2 style={{ marginTop: 6 }}>
            <Field>
              상태
              <select value={r.status} onChange={(e) => setStatus(r, e.target.value)}>
                {RES_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            {r.source === 'MANUAL' ? <Btn style={{ alignSelf: 'end' }} onClick={() => remove(r)}>삭제</Btn> : <span />}
          </Grid2>
        </CardBox>
      ))}
    </Page>
  );
};

export default MobileReservation;
